import React, { useState } from "react";
import type { InventoryCatalogSuggestion } from "../data/types";
import { ProductPriceRecommendationDetail, formatUsageUnitShortLabel, UsageMeasurementUnit  } from "../utils/helpers/productPricing";
import {
  PricingRecommendationDetailModal,
  ProductPriceRecommendationDetailView,
} from "./PricingRecommendationDetailModal";

type PriceRecommendationNoteProps = {
  detail: ProductPriceRecommendationDetail;
  displayUnit: string;
  category: string;
  selectedSuggestion?: InventoryCatalogSuggestion | null;
};

const buildPriceRecommendationSummary = ({
  detail,
  displayUnit,
  category,
  selectedSuggestion,
}: PriceRecommendationNoteProps): string => {
  const normalizedCategory = typeof category === "string" && category ? category : "inventory";
  const effectiveUnit = displayUnit || "unit";

  if (selectedSuggestion) {
    return `Businesses in ${detail.locationLabel} typically price ${selectedSuggestion.name} around $${selectedSuggestion.unit_price.toFixed(2)}/${selectedSuggestion.measure_unit}.`;
  }

  return `Businesses in ${detail.locationLabel} typically price ${normalizedCategory.toLowerCase()} items around $${detail.price.toFixed(2)}/${effectiveUnit}.`;
};

export const PriceRecommendationNote: React.FC<PriceRecommendationNoteProps> = (props) => {
  const { detail, displayUnit, selectedSuggestion } = props;
  const [open, setOpen] = useState(false);

  const summary = buildPriceRecommendationSummary(props);
  const modalDisplayUnit = selectedSuggestion?.measure_unit || displayUnit || "unit";
  const modalItemName = selectedSuggestion?.name;

  return (
    <>
      <p className="inventory-price-note" data-testid="price-recommendation-note">
        {summary} Use this as a starting point - you can always adjust later.{" "}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inventory-price-note-link"
          data-testid="price-recommendation-learn-more"
        >
          Learn more
        </button>
      </p>
      <PricingRecommendationDetailModal
        open={open}
        onClose={() => setOpen(false)}
        title="Product price recommendation"
      >
        <ProductPriceRecommendationDetailView
          detail={detail}
          displayUnit={modalDisplayUnit}
          itemName={modalItemName}
        />
      </PricingRecommendationDetailModal>
    </>
  );
};

type UsagePriceRecommendationNoteProps = {
  detail: ProductPriceRecommendationDetail;
  usageUnit: UsageMeasurementUnit;
  category: string;
};

/** Inline note for operational usage-cost recommendations when Track Usage is enabled. */
export const UsagePriceRecommendationNote: React.FC<UsagePriceRecommendationNoteProps> = ({
  detail,
  usageUnit,
  category,
}) => {
  const [open, setOpen] = useState(false);
  const unitLabel = formatUsageUnitShortLabel(usageUnit as UsageMeasurementUnit);
  const normalizedCategory = typeof category === "string" && category ? category : "inventory";
  const summary = `Kitchens in ${detail.locationLabel} typically see operational costs around $${detail.price.toFixed(2)}/${unitLabel} for ${normalizedCategory.toLowerCase()} usage tracking.`;

  return (
    <>
      <p className="inventory-price-note" data-testid="usage-price-recommendation-note">
        {summary} Use this as a starting point - you can always adjust later.{" "}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inventory-price-note-link"
          data-testid="usage-price-recommendation-learn-more"
        >
          Learn more
        </button>
      </p>
      <PricingRecommendationDetailModal
        open={open}
        onClose={() => setOpen(false)}
        title="Usage cost recommendation"
      >
        <ProductPriceRecommendationDetailView detail={detail} displayUnit={unitLabel} />
      </PricingRecommendationDetailModal>
    </>
  );
};

export { buildPriceRecommendationSummary };
